import { Alert, Box, Button, Card, Divider, FormHelperText, Grid, IconButton, Tooltip, Chip, alpha, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { push } from "connected-react-router";
import { FC } from "react";
import { ListContextProvider, useListContext } from "ra-core";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { Content } from "~/components/content/Content";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { OdResourcePageProps } from "~/types/resource";
import { Datagrid } from "~/components/list/datagrid";
import Scrollbar from "~/components/Scrollbar";
import { FunctionField, TextField } from "~/components/field";
import { RecPatch } from "~/dataProvider/tools";
import { InfoTypeResponseDto } from "~/gen/api/securefile";
import useLocalListController from "~/components/list/useLocalListController";
import { AtopFuncArea, FilterField, FilterOption, SearchField } from "~/components/list/datagridFunctionWidgets";
import usePermitGuard from "~/authProvider/usePermitGuard";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "~/constants";


const infoTypeListOptions : {sorters: Record<string, (a:InfoTypeResponseDto & RecPatch, b:InfoTypeResponseDto & RecPatch) => number>} = {
  sorters: {
    isEnabled: (a, b) => { 
      if(a.isEnabled === b.isEnabled){
        return (a.name || '').toLowerCase() < (b.name || '').toLowerCase() ? -1 : 1
      }else{
        return (a.isEnabled || 0) > (b.isEnabled || 0) ? -1 : 1 
      }
    },
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '').toLowerCase() < (b.name || '').toLowerCase() ? -1 : 1,
  }
}
const baseInfoTypeRes = 'secure-files-base-info-type'

const InfoTypeList: FC<OdResourcePageProps> = (props) => {
  const { resource } = props

  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <ListContextProvider value={useLocalListController({ basePath: '' }, infoTypeListOptions)}>
      <InfoTypeListBody {...props} />
    </ListContextProvider>
  )
}

const InfoTypeListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const listCtx = useListContext()
  if (listCtx.currentSort.field !== 'isEnabled') {
    setTimeout(() => {
      listCtx.setSort('isEnabled')
    })
  }
  const { refetch } = listCtx
  const dispatch = useDispatch()
  const theme = useTheme()
  const rowStyles = (d: any, index: number ) => {
    const bgcolor = 
      d.isBuiltIn ? alpha(theme.palette.warning.light, 0.1) : null
    return bgcolor ? { backgroundColor: bgcolor } : {}
  }
  
  return (
    <Content pageName="File Info Types" resourceName="File Info Types" 
      breadcrumbs={[...(options?.breadcrumbs ?? [])]}
      mainActions={
        <Button
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={() => dispatch(push(`/${resource}/create`))}
        >
          Create File Info Type
        </Button>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 2 }}>
            An Info Type is used to categorize a type of sensitive information, such as social security numbers and email addresses. A file may contain multiple Info Types.
          </Alert>
          <Card>
            <AtopFuncArea >
              <SearchField 
                sourceKey="dbName"
                match={(d: InfoTypeResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
                sx={{ width: 280 }}
                TextFieldProps={{ placeholder: 'Search by Name' }}
              />
              <FilterField 
                label="Status" 
                filterOptions={statusOptions} 
                sourceKey="isEnabled"
                match={(x, filterVal) => {
                  if (filterVal.value == null) { return true }
                  const dto = x as InfoTypeResponseDto
                  return dto.isEnabled === filterVal.value
                }}
                sx={{ width: 140, ml: 'auto' }}
              />
              <FilterField
                label="Built-in or not"
                filterOptions={builtInTypeOptions}
                sourceKey="isBuiltIn"
                match={(x, filterVal) => {
                  if (filterVal.value == null) { return true }
                  const dto = x as InfoTypeResponseDto
                  return dto.isBuiltIn === filterVal.value
                }}
                sx={{ width: 140 }}
              />
            </AtopFuncArea>
            <Divider sx={{ pb: 0 }} />
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& td.column-description span.MuiTypography-root' : { maxWidth:400, wordBreak:'break-all' },
                  }}
                  rowStyle={rowStyles}
                  {...listCtx}
                >
                  <TextField label="Name " source="name" />
                  <TextField label="Display Name" source="displayName" sortable={false} />
                  <TextField label="Description" source="description" sortable={false} />
                  {/* <TextField label="Base InfoType" source="baseInfoType" sortable={false} /> */}
                  <TextField label="Is BuiltIn" source="isBuiltIn" sortable={false} />
                  <FunctionField 
                    source="isEnabled" 
                    label="Status" 
                    sortable={false}
                    render={(record?:RecPatch) => 
                      <Chip 
                        label={(record as InfoTypeResponseDto&RecPatch)?.isEnabled ? 'Enabled' : 'Disabled'}
                        color={((record as InfoTypeResponseDto&RecPatch)?.isEnabled ? 'success' : 'warning') as any}
                      />
                    }
                  />
                  <FunctionField label="Action" sortable={false} 
                    render={(d0?:RecPatch) => {
                      const d = d0 as InfoTypeResponseDto
                      // if (d.isBuiltIn) { return null }
                      return (
                        <Tooltip title="Edit">
                          <IconButton 
                            sx={{ 
                              color: 'text.primary' as any, 
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                            component={RouterLink}
                            to={`/settings/securefile/info-types/${d.id as any}/show`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore 
            <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default InfoTypeList;

const builtInTypeOptions : FilterOption<boolean>[] = [
  { label: "Built-in", value: true },
  { label: "Custom", value: false },
]

const statusOptions : FilterOption<boolean>[] = [
  { label: 'Enabled', value: true },
  { label: 'Disabled', value: false },
]